import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  publishTelegram,
  publishTelegramParcial,
  publishWhatsapp,
  publishFinalWA,
  type Evento,
  type EventsResponse,
  type EventDetail,
  type PodioRunner,
  type Intento,
  type PruebaCombinada,
} from '@/lib/api';
import { parseMarkToNum, isHigherBetter } from '@/lib/marks';
import { esExtranjero } from '@/lib/podios/cruce';
import { TelegramSerieCard, tgCardLayout, type TgChip, type TgFila } from '@/components/TelegramSerieCard';

/* Modal "Publicar en Telegram" (modo admin del directo).
 *
 * Dos publicaciones posibles sobre una prueba:
 *   · SERIE: la clasificación de ESTA unidad tal cual (con Q/q de la fuente,
 *     DNS/DNF/DQ al final y, si es una final, medallas en el top-3).
 *   · CLASIFICADOS: terminadas TODAS las series de la ronda, la lista completa
 *     de quienes progresan (las Q de cada serie + las q por tiempos), ordenada
 *     por marca. Las "q" se calculan aquí con QualiByRank/QualiByTime del RES
 *     si la fuente aún no las ha rellenado (tarda un rato tras la última serie).
 *
 * La tarjeta se rasteriza con html2canvas (offscreen, patrón FichaVivoCard) y
 * se envía por la Cloud Function publish_telegram (whitelist estricta).
 */

/** Partes de la ronda: "Eliminatoria 2/4" → base "eliminatoria", idx 2, total 4. */
function parseRonda(ronda: string): { base: string; idx: number | null; total: number | null } {
  const m = (ronda || '').match(/(\d+)\s*\/\s*(\d+)/);
  const base = (ronda || '')
    .replace(/\d+\s*\/\s*\d+/, '')
    .trim()
    // Grupo de concurso EA ("Clasificación A 1/2" / "B 2/2"): quita la letra de
    // grupo suelta al final para que A y B agrupen como hermanas de la MISMA
    // ronda (Santi 2026-08-10: sin esto no se activaba el resumen de
    // clasificadas del peso). En pista ("Eliminatoria 1/4") no hay letra suelta.
    .replace(/\s+[A-Za-z]$/, '')
    .trim()
    .toLowerCase();
  return { base, idx: m ? +m[1] : null, total: m ? +m[2] : null };
}

function esFinal(ronda: string): boolean {
  const r = (ronda || '').toLowerCase();
  return /final/.test(r) && !/semi/.test(r);
}

/** Clasificación completa de la unidad con la MISMA regla que AtletaEnVivo. */
function campoDe(ev: Evento): PodioRunner[] {
  return ev.resultados && ev.resultados.length ? ev.resultados : ev.podio;
}

/** Chips de la serie de intentos de un concurso (paridad con
 *  auto_directo._chips_intentos): horizontal = una caja por intento con la
 *  mejor destacada; vertical = listón + saltos, destacado el último superado. */
function chipsIntentos(serie: Intento[] | undefined): TgChip[] {
  if (!serie?.length) return [];
  const esVertical = serie.some((x) => (x.height ?? '').trim() !== '');
  if (esVertical) {
    let mejor = -1;
    serie.forEach((x, i) => { if (/o/i.test(x.mark || '')) mejor = i; });
    const out: TgChip[] = [];
    serie.forEach((x, i) => {
      const m = (x.mark || '').trim();
      if (m) out.push({ t: (x.height || '').trim(), b: m, hi: i === mejor });
    });
    return out;
  }
  let mejor = -1;
  let mejorNum = -Infinity;
  serie.forEach((x, i) => {
    const n = parseFloat((x.mark || '').replace(',', '.'));
    if (Number.isFinite(n) && n > mejorNum) { mejorNum = n; mejor = i; }
  });
  return serie.map((x, i) => ({ t: (x.mark || '').trim() || '·', hi: i === mejor }));
}

/** Chips de una combinada: una caja por prueba con marca y puntos; `hi` en
 *  las pruebas que este atleta GANÓ (2026-07-24, paridad con el motor
 *  automático: `maximos[i]` = mejor puntuación de la prueba i entre todos). */
function chipsCombinada(desglose: PruebaCombinada[] | undefined, maximos: number[] = []): TgChip[] {
  const out: TgChip[] = [];
  (desglose ?? []).forEach((x, i) => {
    if (!(x.marca || '').trim()) return;
    const pts = parseInt(x.puntos, 10) || 0;
    out.push({ t: x.marca, b: `${x.puntos} pts`, hi: pts > 0 && pts === (maximos[i] ?? 0) });
  });
  return out;
}

/** MARCHA: un chip ROJO por aviso del juez, en su orden ('~' contacto,
 *  '>' rodilla doblada, 'p' pit lane) — paridad con el motor automático. */
function chipsAvisos(extra: string): TgChip[] {
  return extra.split('').map((ch) => ({ t: ch, rojo: true }));
}

function filaDeRunner(p: PodioRunner, serie?: string | null): TgFila {
  return {
    puesto: p.puesto,
    nombre: p.nombre,
    club: p.club_real || p.club,
    marca: p.marca,
    quali: p.quali ?? null,
    dorsal: p.dorsal ?? null,
    categoria: p.categoria ?? null,
    serie: serie ?? null,
    pais: p.pais ?? null,
  };
}

interface Props {
  data: EventsResponse;
  ev: Evento;
  detalleDe: (key: string) => Promise<EventDetail>;
  onClose: () => void;
  /** Abrir directamente en un modo (botón "Clasificados" del listado). */
  modoInicial?: 'serie' | 'clasificados';
  /** URL de la competición cargada (rfealive.es/sch/<code>): la usa la
   *  publicación PARCIAL vía servidor (concursos por ronda/listón,
   *  combinada por corte de pruebas). */
  compUrl?: string;
  /** Canal de destino (el botón de la tarjeta elige): el modal solo ofrece
   *  publicar en ESE canal — control fino del gasto de WhatsApp (límite del
   *  trial de Whapi). Default telegram. */
  canal?: 'telegram' | 'whatsapp';
}

type Modo = 'serie' | 'clasificados';

const RE_CONCURSO = /longitud|triple|peso|disco|martillo|jabalina|altura|p[ée]rtiga/i;
const RE_COMBINADA = /decatl|heptatl|pentatl|octatl/i;

export function TelegramPublishModal({ data, ev, detalleDe, onClose, modoInicial, compUrl, canal = 'telegram' }: Props) {
  const [modo, setModo] = useState<Modo>(modoInicial ?? 'serie');
  // Publicación PARCIAL vía servidor (mismo render Pillow que el automático).
  const [corte, setCorte] = useState<string>('');
  const [parcialBusy, setParcialBusy] = useState(false);
  const [parcialMsg, setParcialMsg] = useState<string | null>(null);
  const [det, setDet] = useState<EventDetail | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [publicado, setPublicado] = useState(false);
  // WhatsApp (Whapi) con estado propio: se puede publicar en ambos canales.
  const [publicandoWa, setPublicandoWa] = useState(false);
  const [publicadoWa, setPublicadoWa] = useState(false);
  // Tarjeta de FINAL con foto (v4, render server-side): estado propio.
  const [publicandoFinalFoto, setPublicandoFinalFoto] = useState(false);
  const [publicadoFinalFoto, setPublicadoFinalFoto] = useState(false);
  // Estado del modo clasificados (se calcula bajo demanda al entrar).
  const [clasif, setClasif] = useState<{ filas: TgFila[]; nota: string | null; fase: string } | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const final = esFinal(ev.ronda);
  const ronda = parseRonda(ev.ronda);

  // Series HERMANAS de la ronda (misma prueba, misma fase): para saber si la
  // ronda está completa y para agregar los clasificados.
  const hermanas = useMemo(() => {
    if (final) return [ev];
    const out: Evento[] = [];
    const vistas = new Set<string>();
    for (const e of data.eventos) {
      if (e.nombre !== ev.nombre || parseRonda(e.ronda).base !== ronda.base || !e.detalleKey) continue;
      if (vistas.has(e.detalleKey)) continue; // dedupe por si el SCH repite eventos
      vistas.add(e.detalleKey);
      out.push(e);
    }
    return out;
  }, [data, ev, final, ronda.base]);

  const rondaCompleta =
    !final &&
    hermanas.length > 0 &&
    (ronda.total === null || hermanas.length >= ronda.total) &&
    hermanas.every((e) => /oficial/i.test(e.estado || ''));

  // Detalle de ESTA unidad (clasificación completa + DNS + regla de calificación).
  useEffect(() => {
    let cancel = false;
    setCargando(true);
    (ev.detalleKey ? detalleDe(ev.detalleKey) : Promise.resolve(null))
      .then((d) => { if (!cancel) setDet(d); })
      .catch(() => { if (!cancel) setDet(null); })
      .finally(() => { if (!cancel) setCargando(false); });
    return () => { cancel = true; };
  }, [ev, detalleDe]);

  // Filas del modo SERIE: clasificación (detalle si lo hay; si no, el campo
  // del SCH) + los DNS/DNF/DQ al final.
  const filasSerie = useMemo<TgFila[]>(() => {
    const base = det?.clasificacion.length ? det.clasificacion : campoDe(ev);
    // Mejor puntuación por prueba de la combinada (destaca la prueba ganada).
    const maxComb: number[] = [];
    for (const desg of Object.values(det?.combinada ?? {})) {
      desg.forEach((x, i) => {
        const pts = parseInt(x.puntos, 10) || 0;
        if (pts > (maxComb[i] ?? 0)) maxComb[i] = pts;
      });
    }
    const filas = base.map((p) => {
      const fila = filaDeRunner(p);
      const aid = p.athId ?? '';
      fila.records = det?.records?.[aid];
      // Concursos: intentos; total de combinada: marca+puntos de cada
      // prueba; marcha: avisos del juez.
      if (det?.intentos[aid]?.length) fila.chips = chipsIntentos(det.intentos[aid]);
      else if (det?.combinada[aid]?.length) fila.chips = chipsCombinada(det.combinada[aid], maxComb);
      else if (det?.avisos?.[aid]) fila.chips = chipsAvisos(det.avisos[aid]);
      return fila;
    });
    // Medallas de la FINAL: las de la fuente si vienen (Medal 1-3 del RES, ya
    // excluyen a los fuera de concurso); si no, a los 3 primeros que no sean
    // FC (esGuest del RES; país extranjero como respaldo mientras el backend
    // desplegado no mande esGuest). El puesto NO vale: un invitado puede ser
    // 1º sin llevarse el oro (2026-07-25, martillo H).
    if (final) {
      filas.forEach((f, i) => { f.medalla = base[i].medalla ?? null; });
      if (!filas.some((f) => f.medalla)) {
        let n = 0;
        for (let i = 0; i < filas.length && n < 3; i++) {
          const p = base[i];
          if (!filas[i].puesto || (p.esGuest ?? false) || esExtranjero(p.pais)) continue;
          n += 1;
          filas[i].medalla = String(n);
        }
      }
    }
    const irm = (det?.noClasificados ?? []).map((n) => ({
      puesto: '',
      nombre: n.nombre,
      club: n.club,
      marca: '',
      irm: n.estado,
      dorsal: n.dorsal ?? null,
      categoria: n.categoria ?? null,
      // El porqué del DQ/DNF de un marchador: sus avisos, casados por dorsal.
      chips: n.dorsal && det?.avisosDorsal?.[n.dorsal] ? chipsAvisos(det.avisosDorsal[n.dorsal]) : undefined,
    }));
    return [...filas, ...irm];
  }, [det, ev, final]);

  const notaSerie = useMemo(() => {
    const partes: string[] = [];
    if (!final && det?.qualiByRank) {
      let q = `Q = ${det.qualiByRank} primeros de cada serie`;
      if (det.qualiByTime) q += ` · q = ${det.qualiByTime} mejores tiempos`;
      partes.push(q);
    }
    if (Object.keys(det?.avisos ?? {}).length || Object.keys(det?.avisosDorsal ?? {}).length) {
      partes.push('Jueces: ~ pérdida de contacto · > rodilla doblada · p pit lane');
    }
    return partes.length ? partes.join(' · ') : null;
  }, [det, final]);

  // FASE SIGUIENTE (para el título de clasificados): si la prueba tiene
  // semifinales y esto es una eliminatoria → Semifinal; si no → Final.
  const faseSiguiente = useMemo(() => {
    if (/semi/i.test(ronda.base)) return 'la Final';
    const haySemis = data.eventos.some(
      (e) => e.nombre === ev.nombre && /semi/i.test(e.ronda || ''),
    );
    return /elim|serie|ronda/i.test(ronda.base) && haySemis ? 'Semifinales' : 'la Final';
  }, [data, ev, ronda.base]);

  // Agrega los CLASIFICADOS de la ronda al entrar en ese modo.
  useEffect(() => {
    if (modo !== 'clasificados' || clasif || !rondaCompleta) return;
    let cancel = false;
    setCargando(true);
    setError(null);
    Promise.all(hermanas.map((e) => detalleDe(e.detalleKey as string)))
      .then((dets) => {
        if (cancel) return;
        type Cand = { fila: TgFila; num: number | null; quali: string | null };
        const cands: Cand[] = [];
        for (let i = 0; i < dets.length; i++) {
          const serieLabel = parseRonda(hermanas[i].ronda).idx !== null ? `Serie ${parseRonda(hermanas[i].ronda).idx}` : hermanas[i].ronda;
          for (const p of dets[i].clasificacion) {
            cands.push({
              fila: filaDeRunner(p, serieLabel),
              num: parseMarkToNum(ev.nombre, p.marca),
              quali: p.quali ?? null,
            });
          }
        }
        const qByRank = dets[0]?.qualiByRank ?? null;
        const qByTime = dets[0]?.qualiByTime ?? null;
        // Mejor marca primero (tiempos: menor; concursos: mayor).
        const mejorPrimero = (a: Cand, b: Cand) => {
          if (a.num === null && b.num === null) return 0;
          if (a.num === null) return 1;
          if (b.num === null) return -1;
          return isHigherBetter(ev.nombre) ? b.num - a.num : a.num - b.num;
        };
        // Q: lo que diga la fuente — pero el RES las rellena con retraso y
        // pueden venir INCOMPLETAS (2026-07-24, 100 m: 1 q de 4). Si hay
        // menos de las esperadas, se recalcula la lista ENTERA de las
        // posiciones (QualiByRank) en vez de fiarse de la lista a medias.
        let qGrandes = cands.filter((c) => c.quali === 'Q');
        if (qByRank && qGrandes.length < qByRank * hermanas.length) {
          qGrandes = cands.filter((c) => {
            const n = parseInt(c.fila.puesto, 10);
            return Number.isFinite(n) && n <= qByRank;
          }).map((c) => ({ ...c, fila: { ...c.fila, quali: 'Q' } }));
        }
        // q: ídem con los M mejores tiempos entre los no clasificados por
        // puestos (QualiByTime).
        let qPequenas = cands.filter((c) => c.quali === 'q');
        if (qByTime && qPequenas.length < qByTime) {
          const enQ = new Set(qGrandes.map((c) => `${c.fila.nombre}|${c.fila.serie ?? ''}`));
          const resto = cands.filter((c) => !enQ.has(`${c.fila.nombre}|${c.fila.serie ?? ''}`) && c.num !== null);
          resto.sort(mejorPrimero);
          qPequenas = resto.slice(0, qByTime).map((c) => ({ ...c, fila: { ...c.fila, quali: 'q' } }));
        }
        const todos = [...qGrandes, ...qPequenas].sort(mejorPrimero);
        const filas = todos.map((c, i) => ({ ...c.fila, puesto: String(i + 1) }));
        const nota = qByRank
          ? `Q = ${qByRank} primeros de cada serie${qByTime ? ` · q = ${qByTime} mejores tiempos` : ''}`
          : null;
        setClasif({ filas, nota, fase: faseSiguiente });
      })
      .catch((e) => { if (!cancel) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancel) setCargando(false); });
    return () => { cancel = true; };
  }, [modo, clasif, rondaCompleta, hermanas, detalleDe, ev.nombre, faseSiguiente]);

  const cardProps = useMemo(() => {
    if (modo === 'clasificados' && clasif) {
      return {
        competicion: data.competicion,
        titulo: ev.nombre,
        subtitulo: `Clasificados a ${clasif.fase}`,
        filas: clasif.filas,
        medallas: false,
        nota: clasif.nota,
      };
    }
    return {
      competicion: data.competicion,
      titulo: ev.nombre,
      subtitulo: [ev.ronda || 'Final', ev.estado].filter(Boolean).join(' · '),
      viento: ev.viento || null,
      filas: filasSerie,
      medallas: final,
      nota: notaSerie,
    };
  }, [modo, clasif, data.competicion, ev, filasSerie, final, notaSerie]);

  // Corte PARCIAL de un concurso EA, calculado EN CLIENTE (el motor Pillow
  // del servidor no llega a la API de European Athletics: Cloudflare bloquea
  // las IPs de GCP, 2026-07-28). nº de ronda (horizontal) o de listón
  // (vertical); réplica ligera de _corte_horizontal/_corte_vertical.
  const esEA = (ev.detalleKey || '').startsWith('EA:')
    || (ev.detalleKey || '').startsWith('WA:'); // WA (2026-08-05): mismos cortes
  const [corteEA, setCorteEA] = useState<string>('');
  const nCorteEA = corteEA.trim() ? parseInt(corteEA, 10) : 0;
  const esConcursoEA = esEA && modo === 'serie' && !RE_COMBINADA.test(ev.nombre)
    && Object.keys(det?.intentos ?? {}).length > 0;
  const corteEAInfo = useMemo<{ filas: TgFila[]; subtitulo: string } | null>(() => {
    if (!esConcursoEA || !det || nCorteEA < 1) return null;
    const series = det.intentos;
    const porAtleta = new Map(det.clasificacion.map((p) => [p.athId ?? '', p]));
    const vertical = Object.values(series).some((s) => s.some((x) => (x.height ?? '').trim()));
    const cands: { fila: TgFila; num: number }[] = [];
    let subtitulo: string;
    if (vertical) {
      const alturas = [...new Set(Object.values(series).flatMap((s) => s.map((x) => (x.height ?? '').trim()).filter(Boolean)))]
        .sort((a, b) => parseFloat(a) - parseFloat(b)).slice(0, nCorteEA);
      const corteSet = new Set(alturas);
      for (const [aid, serieFull] of Object.entries(series)) {
        const p = porAtleta.get(aid);
        if (!p) continue;
        const serie = serieFull.filter((x) => corteSet.has((x.height ?? '').trim()));
        let mejor = '';
        for (const x of serie) if (/o/i.test(x.mark ?? '')) mejor = (x.height ?? '').trim();
        const fila = filaDeRunner(p);
        fila.marca = mejor || '—';
        fila.quali = null;
        fila.medalla = null;
        fila.chips = chipsIntentos(serie);
        cands.push({ fila, num: mejor ? parseFloat(mejor) : -1 });
      }
      subtitulo = `${ev.ronda || 'Clasificación'} · Listón ${alturas[alturas.length - 1] ?? ''}`;
    } else {
      for (const [aid, serieFull] of Object.entries(series)) {
        const p = porAtleta.get(aid);
        if (!p) continue;
        const serie = serieFull.slice(0, nCorteEA);
        const nums = serie.map((x) => parseFloat((x.mark ?? '').replace(',', '.'))).filter(Number.isFinite);
        const mejor = nums.length ? Math.max(...nums) : -1;
        const fila = filaDeRunner(p);
        fila.marca = mejor > 0 ? mejor.toFixed(2) : '—';
        fila.quali = null;
        fila.medalla = null;
        fila.chips = chipsIntentos(serie);
        cands.push({ fila, num: mejor });
      }
      subtitulo = `${ev.ronda || 'Clasificación'} · Ronda ${nCorteEA}`;
    }
    cands.sort((a, b) => b.num - a.num);
    return { filas: cands.map((c, i) => ({ ...c.fila, puesto: c.num > 0 ? String(i + 1) : '' })), subtitulo };
  }, [esConcursoEA, det, nCorteEA, ev.nombre]);
  const cardPropsFinal = {
    ...(corteEAInfo
      ? { ...cardProps, subtitulo: corteEAInfo.subtitulo, filas: corteEAInfo.filas, medallas: false }
      : cardProps),
    // EA: fondo del Alexander Stadium + logo del Europeo en la esquina, para que
    // el manual replique las tarjetas automáticas (Santi 2026-08-10).
    ...(esEA ? { fondo: '/ea-birmingham26-fondo.jpg', logo: '/ea-birmingham26-logo.png' } : {}),
  };

  // Pie de la foto MÍNIMO (Riki 2026-07-21: repetir el podio bajo la imagen
  // era redundante): una línea con prueba · ronda — campeonato, y el enlace
  // clicable (lo único que la tarjeta no puede ofrecer).
  function caption(conUrl = true): string {
    const comp = data.competicion;
    const que = modo === 'clasificados' && clasif
      ? `${ev.nombre} — Clasificados a ${clasif.fase}`
      // Corte por ronda de concurso EA: el subtítulo lleva "grupo · Ronda N"
      // (Santi 2026-08-10: el caption no reflejaba la ronda).
      : corteEAInfo
      ? `${ev.nombre} · ${corteEAInfo.subtitulo}`
      : `${ev.nombre} · ${ev.ronda || 'Final'}`;
    const base = `⚡ ${que} — ${comp}`;
    // WhatsApp va sin URL (2026-07-23: repetirla en cada post era pesado).
    return conUrl ? `${base}\nresultados.mundoatletismo.es` : base;
  }

  /** Rasteriza la tarjeta offscreen a dataURL PNG (común Telegram/WhatsApp). */
  async function rasterizar(): Promise<string> {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(cardRef.current as HTMLDivElement, {
      width: tgCardLayout(cardPropsFinal.filas).width,
      scale: 2,
      backgroundColor: null,
      useCORS: true,
    });
    return canvas.toDataURL('image/png');
  }

  async function publicar() {
    if (!cardRef.current) return;
    setPublicando(true);
    setError(null);
    try {
      // Solo imagen (2026-07-28, fin de la beta A/B de 2026-07-22: el gemelo
      // en texto duplicaba cada post y el canal se queda con las tarjetas).
      await publishTelegram({
        photo: await rasterizar(),
        caption: caption(),
      });
      setPublicado(true);
      window.setTimeout(onClose, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublicando(false);
    }
  }

  async function publicarWhatsapp() {
    if (!cardRef.current) return;
    setPublicandoWa(true);
    setError(null);
    try {
      // WhatsApp va SOLO en imagen (decisión 2026-07-23, paridad con el
      // automático — formato "imagen" en telegramDirecto/config); este botón
      // es el respaldo manual si el tick fallara durante el Absoluto.
      // stateKey/stateKind marcan el estado del directo con la MISMA clave
      // que usa el tick, para que el automático no repita lo publicado a
      // mano (2026-07-24: semis del 400 duplicadas botón + tick).
      const esClasif = modo === 'clasificados' && !!clasif;
      await publishWhatsapp({
        photo: await rasterizar(),
        caption: caption(false),
        stateKey: esClasif ? `${ev.nombre}|${ronda.base}` : (ev.detalleKey ?? undefined),
        stateKind: esClasif ? 'clasificados' : 'publicados',
      });
      setPublicadoWa(true);
      window.setTimeout(onClose, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublicandoWa(false);
    }
  }

  async function publicarFinalConFoto() {
    if (!ev.detalleKey) return;
    setPublicandoFinalFoto(true);
    setError(null);
    try {
      // Render y envío EN EL SERVIDOR (publish_final_wa): una tarjeta v4 por
      // español con foto en /fotos-finales. No marca el estado del automático
      // (el uso típico es re-publicar cuando la foto llegó tarde).
      await publishFinalWA({ key: ev.detalleKey });
      setPublicadoFinalFoto(true);
      window.setTimeout(onClose, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublicandoFinalFoto(false);
    }
  }

  const sinFilas = (modo === 'serie' ? filasSerie : clasif?.filas ?? []).length === 0;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ld-card" style={{ width: '100%', maxWidth: 720, marginTop: 24 }}>
        <div className="ld-spread" style={{ marginBottom: 10, gap: 10 }}>
          <h2 className="ld-card__title" style={{ margin: 0 }}>
            Publicar en {canal === 'whatsapp' ? 'WhatsApp' : 'Telegram'}
          </h2>
          <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={onClose}>
            Cerrar
          </button>
        </div>
        <p className="ld-card__hint" style={{ marginTop: 0 }}>
          {ev.nombre} · {ev.ronda || 'Final'} · {ev.estado}
        </p>

        {/* Modo: la serie tal cual, o los clasificados de la ronda entera.
            En finales no hay progresión → solo modo serie. En COMBINADAS
            (prueba suelta o total) tampoco hay Q/q → fuera los tabs (Riki
            2026-07-21: no tenían sentido en la longitud del heptatlón). */}
        {!final && !RE_COMBINADA.test(ev.nombre) ? (
          <div className="ld-row" style={{ gap: 8, marginBottom: 12 }}>
            <SegBtn on={modo === 'serie'} onClick={() => setModo('serie')}>Esta serie</SegBtn>
            <SegBtn on={modo === 'clasificados'} onClick={() => setModo('clasificados')} disabled={!rondaCompleta}>
              Clasificados de la ronda
            </SegBtn>
            {!rondaCompleta ? (
              <span className="muted mono" style={{ fontSize: 11, alignSelf: 'center' }}>
                (disponible cuando TODAS las series estén en Oficial)
              </span>
            ) : null}
          </div>
        ) : null}

        {cargando ? (
          <p className="muted" style={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
            <span className="ld-spinner" aria-hidden /> Cargando clasificación…
          </p>
        ) : sinFilas ? (
          <p className="ld-card__hint">Sin resultados que publicar todavía.</p>
        ) : (
          <>
            {esConcursoEA ? (
              <div className="ld-row" style={{ gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min={1}
                  value={corteEA}
                  onChange={(e) => setCorteEA(e.target.value)}
                  placeholder="completo"
                  style={{ width: 100 }}
                />
                <span className="muted mono" style={{ fontSize: 11 }}>
                  Corte parcial: nº de ronda de intentos (horizontal) o nº de listón, en orden (vertical) · vacío = concurso completo
                </span>
              </div>
            ) : null}
            <ScaledPreview cardW={tgCardLayout(cardPropsFinal.filas).width}>
              <TelegramSerieCard {...cardPropsFinal} />
            </ScaledPreview>
            {/* Gemelo a tamaño real, fuera de pantalla, para html2canvas. */}
            <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
              <TelegramSerieCard ref={cardRef} {...cardPropsFinal} />
            </div>
            <div className="ld-row" style={{ marginTop: 14, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {canal === 'whatsapp' ? (
                <button
                  type="button"
                  className="ld-btn ld-btn--primary"
                  disabled={publicandoWa || publicadoWa}
                  onClick={() => void publicarWhatsapp()}
                >
                  {publicadoWa ? 'Publicado ✓' : publicandoWa ? 'Publicando…' : 'Publicar en WhatsApp'}
                </button>
              ) : null}
              {canal === 'whatsapp' && final && (ev.detalleKey || '').startsWith('WA:') ? (
                <button
                  type="button"
                  className="ld-btn"
                  disabled={publicandoFinalFoto || publicadoFinalFoto}
                  onClick={() => void publicarFinalConFoto()}
                  title="Tarjeta v4 con el recorte del atleta (una por español con foto en /fotos-finales). Render en el servidor."
                >
                  {publicadoFinalFoto ? 'Final con foto ✓' : publicandoFinalFoto ? 'Publicando…' : 'Final con foto'}
                </button>
              ) : null}
              {canal !== 'whatsapp' ? (
                <button
                  type="button"
                  className="ld-btn ld-btn--primary"
                  disabled={publicando || publicado}
                  onClick={() => void publicar()}
                >
                  {publicado ? 'Publicado ✓' : publicando ? 'Publicando…' : 'Publicar en Telegram'}
                </button>
              ) : null}
              <span className="muted mono" style={{ fontSize: 11 }}>
                Se envía la tarjeta como imagen al canal configurado.
              </span>
            </div>
          </>
        )}
        {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}

        {/* Publicación PARCIAL vía servidor: concursos (clasificación cortada
            en una ronda / listón) y combinada (acumulada tras N pruebas). Usa
            el MISMO render Pillow del motor automático — sirve para probar
            con el sub-20 exactamente lo que publicará el tick del directo.
            Publica en el canal del modal (2026-07-25: antes solo Telegram). */}
        {(() => {
          const segs = (ev.detalleKey || '').split(':');
          const individual = segs.length >= 3 && segs[1] === segs[2];
          const esCombTotal = individual && RE_COMBINADA.test(ev.nombre);
          const esConcursoEv = individual && !esCombTotal && RE_CONCURSO.test(ev.nombre);
          if ((!esConcursoEv && !esCombTotal) || !compUrl || !ev.detalleKey) return null;
          // (Riki 2026-07-21) El corte va SIEMPRE por número de orden — en
          // verticales es el Nº de listón (1 = primero), NO la altura.
          const etiqueta = esCombTotal
            ? 'Nº de pruebas del corte (vacío = todas las registradas)'
            : 'Nº de ronda o Nº de listón, en orden (no la altura) · vacío = último completo';
          const publicarParcial = async () => {
            setParcialBusy(true);
            setParcialMsg(null);
            try {
              const res = await publishTelegramParcial({
                url: compUrl,
                key: ev.detalleKey as string,
                corte: corte.trim() ? parseInt(corte, 10) : undefined,
                canal,
              });
              setParcialMsg(res);
            } catch (e) {
              setParcialMsg(e instanceof Error ? e.message : String(e));
            } finally {
              setParcialBusy(false);
            }
          };
          return (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--ld-line)' }}>
              <p className="ld-card__hint" style={{ marginTop: 0 }}>
                Publicación parcial (motor automático — la tarjeta se genera en el servidor
                {esCombTotal ? ', clasificación acumulada de la combinada' : ', clasificación cortada en la ronda/listón'}).
              </p>
              <div className="ld-row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min={1}
                  value={corte}
                  onChange={(e) => setCorte(e.target.value)}
                  placeholder="auto"
                  title={etiqueta}
                  style={{ width: 90 }}
                />
                <button
                  type="button"
                  className="ld-btn ld-btn--primary ld-btn--small"
                  disabled={parcialBusy}
                  onClick={() => void publicarParcial()}
                >
                  {parcialBusy ? 'Publicando…' : 'Publicar parcial'}
                </button>
                <span className="muted mono" style={{ fontSize: 11 }}>{etiqueta}</span>
              </div>
              {parcialMsg ? <p className="muted mono" style={{ fontSize: 12, marginTop: 8 }}>{parcialMsg}</p> : null}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/** Preview escalado de la tarjeta (anchura real 1080 → cabe en el modal). */
function ScaledPreview({ cardW, children }: { cardW: number; children: ReactNode }) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.3);
  const [alto, setAlto] = useState(200);
  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const update = () => {
      const s = outer.clientWidth / cardW;
      setScale(s);
      setAlto(inner.offsetHeight * s);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [children]);
  return (
    <div
      ref={outerRef}
      style={{ width: '100%', height: alto, overflow: 'hidden', borderRadius: 10, border: '1px solid var(--ld-line)' }}
    >
      <div ref={innerRef} style={{ width: cardW, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
}

function SegBtn({ on, onClick, disabled, children }: { on: boolean; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      className={`ld-btn ld-btn--small ${on ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
